import { useFormContext } from 'react-hook-form';
import type { Flow } from '../types';
import { usePagination } from 'hooks/usePagination';
import { useFlows } from '../api';
import { useFlowsColumns } from '../hooks/useFlowsColumns';
import { useGridSelection } from '../hooks';
import { BaseGrid } from 'components/common';
import { SuspenseBoundary } from 'components/common';
import { useNavigate } from 'react-router';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useListParams } from 'hooks';
import { debounce } from 'lodash-es';
import { usePermissionGuard } from 'features/login';

const FlowsGridComponent = memo(() => {
  const { accessToChangeFlow } = usePermissionGuard();
  const pagination = usePagination([], 50);
  const navigate = useNavigate();
  const { watch } = useFormContext();
  const { setQ, setApps, q: urlQ, apps: urlApps } = useListParams();
  const [debouncedFilters, setDebouncedFilters] = useState<{
    q: string | undefined;
    apps: string[] | undefined;
  }>({
    q: urlQ ?? undefined,
    apps: (urlApps as string[]) ?? undefined,
  });

  const filterByQ = watch('q') as string;
  const filterByApps = watch('apps') as string[];

  const debouncedUpdate = useCallback(
    (q: string, apps?: string[]) => {
      setDebouncedFilters({ q, apps: apps ?? undefined });
      setQ(q ?? undefined);
      setApps(apps ?? undefined);
    },
    [setDebouncedFilters, setQ, setApps],
  );

  const debouncedFn = useMemo(
    () => debounce(debouncedUpdate, 500),
    [debouncedUpdate],
  );

  useEffect(() => {
    debouncedFn(filterByQ, filterByApps);
    return () => {
      debouncedFn.cancel();
    };
  }, [filterByQ, filterByApps, debouncedFn]);

  const { selectedRows, handleSelectionChange } =
    useGridSelection('selectedAdsHistory');

  const columns = useFlowsColumns();
  const { desc } = useListParams();

  const {
    data: { data: flows, count },
    isLoading,
  } = useFlows({
    params: {
      ...(debouncedFilters.q && { name: debouncedFilters.q }),
      ...(debouncedFilters.apps && { apps: debouncedFilters.apps }),
      desc,
      order_by: 'created',
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const handleClick = useCallback(
    (id: number) => {
      accessToChangeFlow && navigate(`/cabinet/facebook/flows/${id}`);
    },
    [navigate, accessToChangeFlow],
  );

  return (
    <BaseGrid<Flow>
      rows={flows}
      columns={columns}
      count={count}
      isLoading={isLoading}
      pagination={pagination}
      rowSelectionModel={selectedRows}
      onRowSelectionModelChange={handleSelectionChange}
      getRowId={(row) => row.id}
      onRowClick={(params) => {
        handleClick((params.row as Flow).id);
      }}
    />
  );
});

FlowsGridComponent.displayName = 'FlowsGridComponent';

const FlowsGrid = () => {
  return (
    <SuspenseBoundary>
      <FlowsGridComponent />
    </SuspenseBoundary>
  );
};

export default FlowsGrid;
